import type { ReactNode } from 'react';

export type Row = Record<string, unknown>;

/** Numbers get thousands separators (2 decimals when fractional); everything else prints as-is. */
export function fmtCell(v: unknown): string {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'number') {
    return Number.isInteger(v)
      ? v.toLocaleString()
      : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(v);
}

type Tier = 'ok' | 'warn' | 'error' | 'info';

const TIER_CLASS: Record<Tier, string> = {
  ok: 'bg-green-100 text-green-800',
  warn: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
};

/** Colours a status / reason cell by what it says (mismatches red, matches green, pending amber). */
export function tierFor(value: unknown): Tier | null {
  const s = String(value ?? '');
  if (/mismatch|not adjusted|not reversed|not found|missing|variance|overdue|not_in_bank|unmatched/i.test(s)) return 'error';
  if (/only \(/i.test(s)) return 'error';
  if (/^matched$|matched$|reconciled|^ok$/i.test(s)) return 'ok';
  if (/not due|not_yet_due|pending|awaiting|duplicate/i.test(s)) return 'warn';
  return null;
}

export function Pill({ value, tier }: { value: unknown; tier?: Tier | null }) {
  const t = tier ?? tierFor(value);
  if (!t) return <>{fmtCell(value)}</>;
  return <span className={`rounded px-1.5 py-0.5 font-medium ${TIER_CLASS[t]}`}>{fmtCell(value)}</span>;
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'bad' }) {
  return (
    <div className="rounded-lg border border-border bg-background px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${tone === 'bad' ? 'text-red-700' : 'text-foreground'}`}>{value}</div>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

interface RecordTableProps {
  rows: Row[];
  /** Columns shown as coloured pills. */
  statusCols?: string[];
  /** Rows rendered at most; the Excel report always has everything. */
  max?: number;
}

/** A table whose columns come from the first row's keys. */
export function RecordTable({ rows, statusCols = ['Status', 'Reason'], max = 300 }: RecordTableProps) {
  if (rows.length === 0) return null;
  const columns = Object.keys(rows[0]);
  return (
    <div className="max-h-[420px] overflow-auto rounded-md border border-border bg-background">
      <table className="w-full whitespace-nowrap text-left text-xs">
        <thead className="sticky top-0 bg-muted text-muted-foreground">
          <tr>
            {columns.map((c) => (
              <th key={c} className="px-3 py-1.5 font-semibold">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, max).map((row, i) => (
            <tr key={i} className="border-t border-border">
              {columns.map((c) => (
                <td key={c} className="px-3 py-1.5 text-foreground">
                  {statusCols.includes(c) ? <Pill value={row[c]} /> : fmtCell(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A labelled input used by the options rows of the reconciliation panels. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
      {label}
      {children}
    </label>
  );
}

export const inputClass =
  'rounded-md border border-border bg-background px-3 py-2 text-sm font-normal text-foreground';
